import { useMemo } from 'react';
import { ArrowUpRightIcon } from '@phosphor-icons/react';
import { assignCategoryColors } from '@budget/core';
import type { Link } from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { useScope } from '../hooks/useScope';
import { CategoryBadge } from '../components/CategoryBadge';
import { ThirdPartyLogo } from '../components/ThirdPartyLogos';

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function LinkCard({ link, color }: { link: Link; color: string }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="stat-card group relative no-underline text-inherit flex flex-col gap-3 transition-colors hover:bg-overlay"
    >
      <ArrowUpRightIcon
        size={20}
        weight="bold"
        className="absolute top-3.5 right-3.5 text-fg-tertiary group-hover:text-fg transition-colors"
      />
      <div className="flex items-start gap-3 pr-8">
        <span className="w-7 h-7 flex-none flex items-center justify-center">
          <ThirdPartyLogo location={link.name} size={28} />
        </span>
        <div className="flex flex-col gap-3 min-w-0">
          <div className="flex flex-col gap-1.5">
            <span className="font-semibold text-base text-fg leading-tight">
              {link.name}
            </span>
            <span className="text-xs tabular text-fg-tertiary truncate">
              {hostnameOf(link.url)}
            </span>
          </div>
          {link.purpose && (
            <CategoryBadge label={link.purpose} color={color} className="self-start" />
          )}
        </div>
      </div>
    </a>
  );
}

export function LinksPage() {
  const { data, isLoading, error } = useDashboard();
  const { scope } = useScope();
  const allLinks = data?.links ?? [];

  // Household-tagged links are "shared" — they surface under every scope.
  // Person-tagged links only show under their own person's scope (and
  // household). Colors and grouping are computed from the filtered list so
  // sections empty themselves out when nothing in them matches the scope.
  const links = useMemo(() => {
    if (scope === 'household') return allLinks;
    return allLinks.filter((l) => l.person === 'household' || l.person === scope);
  }, [allLinks, scope]);

  // Stable color per purpose so links of the same kind share a tag color.
  const colorMap = useMemo(
    () => assignCategoryColors(links.map((l) => l.purpose)),
    [links]
  );

  // Group by purpose for visual structure. Order follows the sheet — the
  // user groups rows by type there, so each purpose's first row dictates
  // where its section appears.
  const groups = useMemo(() => {
    const seen = new Map<string, Link[]>();
    for (const l of links) {
      const key = l.purpose || 'Other';
      if (!seen.has(key)) seen.set(key, []);
      seen.get(key)!.push(l);
    }
    return Array.from(seen.entries());
  }, [links]);

  return (
    <div className="page-container space-y-8">
      <header className="page-header">
        <div>
          <h1 className="page-title">Links</h1>
          <p className="page-subtitle">Bookmarks and tools collected in the spreadsheet</p>
        </div>
      </header>

      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">Loading…</div>
      )}

      {data && links.length === 0 && (
        <div className="card empty-state">
          {allLinks.length === 0
            ? 'No links in the sheet yet — add a row with a hyperlinked title and a purpose.'
            : 'No links tagged for this scope. Switch the toggle in the topbar to see other links.'}
        </div>
      )}

      {groups.map(([purpose, items]) => (
        <section key={purpose} className="flex flex-col gap-3">
          <h2 className="text-sm uppercase tracking-[0.08em] font-medium text-fg-tertiary">
            {purpose}
          </h2>
          <div className="grid grid-cols-3 gap-3 max-md:grid-cols-2 max-sm:grid-cols-1">
            {items.map((l) => (
              <LinkCard
                key={l.url}
                link={l}
                color={colorMap.get(l.purpose) ?? 'var(--berg-text-secondary)'}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
